import { useState } from "react";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BadgeCheck, Camera, Loader2, ShieldCheck } from "lucide-react";

export default function PhotoVerificationCard() {
  const { user, checkAuth } = useAuth();
  const [verifying, setVerifying] = useState(false);
  const [failReason, setFailReason] = useState(null);

  const handleSelfie = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image");
      return;
    }
    setVerifying(true);
    setFailReason(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/verify-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.verified) {
        toast.success("You're verified! The badge now shows on your profile.");
        checkAuth();
      } else {
        setFailReason(response.data.reason);
        toast.error("Verification didn't pass — see the tip below and try again");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setVerifying(false);
      e.target.value = "";
    }
  };

  if (user?.is_verified) {
    return (
      <div className="glass-card p-6 mt-6 border border-green-500/20" data-testid="photo-verification-card">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-green-500/15 flex items-center justify-center">
            <BadgeCheck className="h-6 w-6 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              Photo Verified
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">Active</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Your Verified badge is visible to everyone on your profile
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-6 mt-6" data-testid="photo-verification-card">
      <div className="flex items-center gap-3 mb-3">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold">Get Verified</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Take a quick selfie and our AI checks it against your profile photo. Pass the check and earn the{" "}
        <BadgeCheck className="inline w-4 h-4 text-primary" /> Verified badge — members trust verified profiles more.
      </p>

      {failReason && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400 mb-4" data-testid="verification-fail-reason">
          {failReason} — try again with good lighting, facing the camera directly.
        </div>
      )}

      <label className="block">
        <input
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={handleSelfie}
          disabled={verifying}
          data-testid="selfie-input"
        />
        <Button asChild className="w-full bg-primary hover:bg-primary/90" disabled={verifying}>
          <span className="cursor-pointer" data-testid="verify-selfie-btn">
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking your selfie... (~10s)
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" /> Take a Selfie to Verify
              </>
            )}
          </span>
        </Button>
      </label>
      <p className="text-xs text-muted-foreground mt-3">
        Your selfie is used only for verification and is never shown on your profile. You need at least one profile photo first.
      </p>
    </div>
  );
}
